import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductListDataView, getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cenAttributeFinalDataHandler from "@tCentro/Handlers/CENAttributeFinalDataHandler";
import cenAttributeNamesHandler from "@tCentro/Handlers/CENAttributeNamesHandler";
import cenAttributeSearchTermHandler from "@tCentro/Handlers/CENAttributeSearchTermHandler";
import SearchIcon from "@tCentro/Icons/SearchIcon";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps { }

const mapStateToProps = (state: CustomApplicationState) => {
    const attributeSearchText = state.t_components?.attributeSearchText;
    const attributeNames = state.t_components?.attributeNames;
    return {
        loaded: !!getProductListDataView(state).value,
        attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
        brandFacets: getProductListDataViewProperty(state, "brandFacets"),
        categoryFacets: getProductListDataViewProperty(state, "categoryFacets"),
        attributeSearchText,
        attributeNames,
    };
};

const mapDispatchToProps = {
    cenAttributeSearchTermHandler,
    cenAttributeNamesHandler,
    cenAttributeFinalDataHandler
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListSearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    filterTag?: TagPresentationProps;
    clearAllLink?: LinkPresentationProps;
}

export const searchWithinStyles: ProductListSearchWithinStyles = {
    wrapper: {
        css: css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    padding: 15px;
                `,
                css`
                    padding: 15px;
                    padding-bottom: 0px;
                `,
                null,
                null,
            ],"max",)}
        div[data-test-selector^="attributeFilterSearch"] {
            display: flex;
            align-items: center;
            border-radius: 20px !important;
            >button{
                min-width: auto;
            }
        }
        `
    },
    titleText: {
        variant: "h6",
    },
    searchTextField: {
        iconProps: { src: SearchIcon },
        cssOverrides: {
            formField: css`
                width: 100%;
                margin-bottom: 15px;
                div {
                    input {
                        border-radius: 40px !important;
                        padding: 10px 16px !important;
                        padding-right: 32px !important;
                        height: 32px;
                        background: ${COLORS.background.grayShade} !important;
                        color: ${COLORS.black} !important;
                        font-size: ${TYPOGRAPHY.size.smaller} !important;
                        font-weight: ${TYPOGRAPHY.weight.base};
                    }
                    span {
                        top: 10px !important;
                        right: 12px !important;
                        width: 12px !important;
                        height: 12px !important;
                        padding: 0px !important;
                        svg {
                            width: 12px;
                            height: 12px;
                        }
                    }
                }
            `,
        },
    },
};

const styles = searchWithinStyles;

const AttributesFilterSearchWithin: FC<Props> = ({ loaded, attributeTypeFacets, brandFacets, categoryFacets, attributeSearchText, cenAttributeSearchTermHandler, cenAttributeNamesHandler, attributeNames, cenAttributeFinalDataHandler }) => {
    if (!loaded) {
        return null;
    }
    const [filter, setFilter] = useState("");

    const attributeValues = [
        ...(attributeTypeFacets?.map(attribute => attribute.name) ?? []),
        ...(categoryFacets?.length ? ["Categories"] : []),
        ...(brandFacets?.length ? ["Brands"] : []),
    ];

    useEffect(() => {
        cenAttributeNamesHandler({ data: attributeValues });
    }, []);

    useEffect(() => {
        setFilter("");
        cenAttributeSearchTermHandler({ data: [] });
        cenAttributeFinalDataHandler({ data: attributeValues });
    }, [categoryFacets, attributeTypeFacets, brandFacets])

    const doSearch = () => {
        if (filter.trim() !== "") {
            const newSearchTerms = [...attributeSearchText?.searchText ?? [], filter];
            cenAttributeSearchTermHandler({ data: newSearchTerms });
            // eslint-disable-next-line no-unsafe-optional-chaining
            const finalData = [...attributeNames?.attributeNameValues];
            const filteredAttributeNames = finalData.filter((facet: any) => newSearchTerms.some((term: string) => facet.toLowerCase().includes(term.toLowerCase())));
            cenAttributeFinalDataHandler({ data: filteredAttributeNames });
        }
        setFilter("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setFilter(event.currentTarget.value);
        }
    };

    const textFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFilter(value);
        const filteredAttributeNames = attributeNames?.attributeNameValues?.filter((facet: any) => facet?.toLowerCase().includes(value.toLowerCase()));
        cenAttributeFinalDataHandler({ data: filteredAttributeNames });
    }

    const removeFilter = (index: number) => {
        const remainingSearchTerms = attributeSearchText?.searchText?.filter((_: any, i: any) => i !== index) ?? [];
        cenAttributeSearchTermHandler({ data: remainingSearchTerms });
        // eslint-disable-next-line no-unsafe-optional-chaining
        const remainingData = [...attributeNames?.attributeNameValues];
        const filteredAttributeNames = remainingData.filter((facet: any) =>
            remainingSearchTerms.some((term: string) => facet.toLowerCase().includes(term.toLowerCase()))
        );
        if (filteredAttributeNames.length) {
            cenAttributeFinalDataHandler({ data: filteredAttributeNames });
        } else {
            cenAttributeFinalDataHandler({ data: attributeValues });
        }
    }

    const clearAll = () => {
        cenAttributeSearchTermHandler({ data: [] });
        cenAttributeFinalDataHandler({ data: attributeValues });
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextField
                {...styles.searchTextField}
                iconClickableProps={{ onClick: doSearch }}
                id="attributesKeywordSearch"
                onChange={(e) => textFieldChange(e)}
                value={filter}
                placeholder={translate("Search")}
                type="text"
                data-test-selector="attributeFiltersSearchWithin"
                onKeyDown={handleKeyDown}
            />
            {attributeSearchText?.searchText?.length > 0 && attributeSearchText?.searchText?.map((term: string, index: number) => (
                <Tag
                    key={index}
                    {...styles.filterTag}
                    onDelete={() => removeFilter(index)}
                    data-test-selector="attributeFilterSearch"
                >
                    {translate("Search")}: {term}
                </Tag>
            ))}
            {attributeSearchText?.searchText?.length > 0 && (
                <Link
                    {...styles.clearAllLink}
                    onClick={clearAll}
                    data-test-selector="attributeClearAll"
                >
                    {translate("Clear Filter Search")}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AttributesFilterSearchWithin),
    definition: {
        group: "Product List",
        displayName: "Attributes Filter Search Within",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
